import nodeService from '@/services/nodejs'
import {
  OFFICIAL_SCORE_SLUG,
  OPEN_TEXT_SLUG,
  humanizeSlugTitle,
  ratingBuckets,
  resolveSurveySlug,
  surveySlugTitles,
} from './survey-questions'

/**
 * Valores provisórios: a rota /survey/nps-report ainda não devolve o funil de
 * cobertura nem os reembolsos. Quando os campos chegarem, o retorno da API
 * passa a ter precedência e estes números deixam de ser usados.
 */
const COVERAGE_FALLBACK = {
  total_visitors: 2500,
  total_responses_opened: 2000,
  total_refunded: 20,
}

const NON_CHART_TYPES = ['text', 'file', 'file-image', 'img', 'hidden_config']

/**
 * Léxicos usados na classificação dos comentários. O sufixo `*` casa prefixo
 * (`sinaliz*` cobre sinalização, sinalizado, sinalizar) e os termos sem sufixo
 * casam palavra inteira, para que "mal" não seja encontrado em "animal".
 */
const POSITIVE_TERMS = [
  'maravilhos*',
  'excelente*',
  'lind*',
  'perfeit*',
  'otim*',
  'incrivel',
  'incriveis',
  'bom',
  'boa',
  'amei',
  'adorei',
  'amamos',
  'gostei',
  'gostamos',
  'sensacional',
  'espetacular',
  'encantador*',
  'recomendo',
  'recomendamos',
  'atencios*',
  'prestativ*',
  'educad*',
  'simpatic*',
  'acolhedor*',
  'organizad*',
  'limp*',
  'paraiso',
  'top',
  'nota 10',
  'parabens',
  'voltarei',
  'voltaremos',
  'satisfeit*',
]

const NEGATIVE_TERMS = [
  'lixo',
  'sujeira*',
  'sujo',
  'sujos',
  'suja',
  'sujas',
  'ruim',
  'pessim*',
  'horrivel',
  'grosseir*',
  'absurd*',
  'demora*',
  'demorou',
  'mal',
  'pouco caso',
  'descaso',
  'abandonad*',
  'quebrad*',
  'estragad*',
  'caro',
  'cara',
  'fila',
  'filas',
  'perigos*',
  'insegur*',
  'despreparad*',
  'mau atendimento',
  'nao recomendo',
  'nao gostei',
  'decepc*',
  'reclamacao',
  'problema*',
  'faltou',
  'falta',
  'sem estrutura',
  'sem sinalizacao',
  'confus*',
  'bagunc*',
  'esperava mais',
  'deixou a desejar',
  'nada demais',
  'nao valeu',
]

const SUGGESTION_TERMS = [
  'melhorar',
  'melhoria*',
  'poderia*',
  'podia',
  'deveria*',
  'devia',
  'precisa*',
  'precisaria',
  'sugiro',
  'sugest*',
  'seria bom',
  'seria interessante',
  'seria legal',
  'falta',
  'faltou',
  'colocar',
  'instalar',
  'aumentar',
  'ampliar',
  'disponibilizar',
  'seria otimo',
  'ideia',
]

const STOPWORDS = new Set([
  'o',
  'a',
  'e',
  'é',
  'os',
  'as',
  'de',
  'da',
  'no',
  'na',
  'do',
  'em',
  'ao',
  'um',
  'dos',
  'das',
  'uma',
  'foi',
  'que',
  'com',
  'nos',
  'nas',
  'por',
  'aos',
  'para',
  'mais',
  'acho',
  'como',
  'muito',
  'muita',
  'menos',
  'sobre',
  'gente',
  'vimos',
  'deveria',
  'tudo',
  'todo',
  'toda',
  'esse',
  'essa',
  'este',
  'esta',
  'isso',
  'pelo',
  'pela',
  'mas',
  'nao',
  'sim',
  'ser',
  'estava',
  'lugar',
])

/**
 * Palavras ignoradas no comparativo de termos e na nuvem de tags.
 * Complementa STOPWORDS: qualquer termo presente em qualquer uma das duas
 * listas não entra no agrupamento de frequência.
 */
const WORD_CLOUD_IGNORE = new Set([
  'onde',
  'quem',
  'como',
  'porque',
  'porquê',
  'porquês',
  'porquês',
  'sendo',
])

function isIgnoredWordCloudTerm(word) {
  return STOPWORDS.has(word) || WORD_CLOUD_IGNORE.has(word)
}

/**
 * Temas padrão do relatório. Cada tema agrupa sinônimos, o que evita depender
 * de uma única palavra e permite somar citações escritas de formas diferentes.
 * Tópicos digitados pelo gestor são acrescentados a esta lista, nunca a
 * substituem.
 */
const DEFAULT_TOPICS = [
  {
    key: 'sinalizacao',
    label: 'Sinalização',
    terms: ['sinaliz*', 'placa*', 'mapa*'],
  },
  {
    key: 'limpeza',
    label: 'Limpeza e lixo',
    terms: ['lixo', 'lixeira*', 'sujeira', 'suj*', 'limpez*', 'residuo*'],
  },
  {
    key: 'banheiro',
    label: 'Banheiros',
    terms: ['banheiro*', 'sanitario*', 'vestiario*'],
  },
  {
    key: 'seguranca',
    label: 'Segurança',
    terms: ['seguranc*', 'acidente*', 'perigo*', 'insegur*', 'salva-vidas'],
  },
  {
    key: 'guia',
    label: 'Guias e condutores',
    terms: ['guia*', 'condutor*', 'monitor*'],
  },
  {
    key: 'natureza',
    label: 'Belezas naturais',
    terms: ['natureza', 'beleza*', 'paisagem*', 'cachoeira*', 'trilha*', 'rio'],
  },
  {
    key: 'atendimento',
    label: 'Atendimento',
    terms: ['atendimento', 'recepc*', 'funcionario*', 'equipe', 'atendente*'],
  },
  {
    key: 'preco',
    label: 'Preço e ingresso',
    terms: ['preco*', 'caro', 'ingresso*', 'valor', 'taxa'],
  },
  {
    key: 'estrutura',
    label: 'Estrutura e acesso',
    terms: ['estrutura*', 'escada*', 'corrimao', 'acesso*', 'estacionamento'],
  },
]

function normalizeText(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const termPatternCache = {}

function termPattern(term) {
  if (termPatternCache[term]) return termPatternCache[term]

  const normalized = normalizeText(term)
  const isPrefix = normalized.endsWith('*')
  const base = escapeRegExp(isPrefix ? normalized.slice(0, -1) : normalized)
  const suffix = isPrefix ? '[a-z0-9]*' : ''

  termPatternCache[term] = new RegExp(
    `(^|[^a-z0-9])${base}${suffix}([^a-z0-9]|$)`
  )

  return termPatternCache[term]
}

function matchesAnyTerm(normalizedText, terms) {
  return terms.some((term) => termPattern(term).test(normalizedText))
}

function toWeight(count) {
  const weight = Number(count)
  return Number.isFinite(weight) && weight > 0 ? weight : 1
}

function totalWeight(entries) {
  return entries.reduce((total, entry) => total + toWeight(entry.count), 0)
}

/* ------------------------------------------------------------------ *
 * Perguntas de nota
 * ------------------------------------------------------------------ */

function buildSlugTitles() {
  return Object.keys(surveySlugTitles).reduce((acc, slug) => {
    acc[slug] = { ...surveySlugTitles[slug] }
    return acc
  }, {})
}

function detectScale(value) {
  let max = 0
  Object.keys(value || {}).forEach((key) => {
    const numeric = Number(key)
    if (Number.isFinite(numeric)) max = Math.max(max, numeric)
  })

  return max
}

function buildDistribution(value, scale) {
  const distribution = {}
  for (let score = 1; score <= scale; score++) distribution[score] = 0

  Object.keys(value || {}).forEach((key) => {
    if (key === 'average') return
    const score = Number(key)
    const count = Number(value[key])
    if (!Number.isFinite(score) || !Number.isFinite(count)) return
    if (distribution[score] == undefined) distribution[score] = 0
    distribution[score] += count
  })

  return distribution
}

function sumDistribution(distribution, scores) {
  return scores.reduce(
    (total, score) => total + (Number(distribution[score]) || 0),
    0
  )
}

function calculateAverage(distribution) {
  let weighted = 0
  let total = 0

  Object.keys(distribution).forEach((score) => {
    const value = Number(score)
    const count = Number(distribution[score]) || 0
    if (!Number.isFinite(value)) return
    weighted += value * count
    total += count
  })

  return total > 0 ? weighted / total : 0
}

function buildChart(distribution) {
  return {
    labels: Object.keys(distribution),
    datasets: [
      {
        label: 'Respostas',
        data: Object.values(distribution),
      },
    ],
  }
}

function getAverageLevel(normalized) {
  if (normalized >= 87.5) return { label: 'Excelente', color: '#20824f' }
  if (normalized >= 75) return { label: 'Boa', color: '#5aa469' }
  if (normalized >= 50) return { label: 'Regular', color: '#e67e22' }
  return { label: 'Crítica', color: '#d93051' }
}

function getCsatLevel(csat) {
  if (csat >= 90) return { label: 'Excelente', color: '#20824f' }
  if (csat >= 75) return { label: 'Boa', color: '#5aa469' }
  if (csat >= 60) return { label: 'Regular', color: '#e67e22' }
  return { label: 'Baixa', color: '#d93051' }
}

function buildRatingQuestion(slug, meta, value) {
  const scale = meta.scale > 1 ? meta.scale : detectScale(value) || 5
  const distribution = buildDistribution(value, scale)
  const total = sumDistribution(distribution, Object.keys(distribution))

  if (total === 0) return null

  const buckets = ratingBuckets(scale)
  const negative = sumDistribution(distribution, buckets.negative)
  const neutral = sumDistribution(distribution, buckets.neutral)
  const positive = sumDistribution(distribution, buckets.positive)

  const average = calculateAverage(distribution)
  const normalized = scale > 1 ? ((average - 1) / (scale - 1)) * 100 : 0
  const csat = (positive / total) * 100
  const negativeShare = (negative / total) * 100

  return {
    slug,
    title: meta.title,
    session: meta.session || '',
    order: meta.order || 900,
    scale,
    distribution,
    chartData: buildChart(distribution),
    total,
    positive,
    neutral,
    negative,
    average: Number(average.toFixed(2)),
    normalized: Number(normalized.toFixed(2)),
    csat: Number(csat.toFixed(2)),
    negativeShare: Number(negativeShare.toFixed(2)),
    averageLevel: getAverageLevel(normalized),
    csatLevel: getCsatLevel(csat),
    positiveScores: buckets.positive,
    negativeScores: buckets.negative,
  }
}

/* ------------------------------------------------------------------ *
 * Respostas abertas
 * ------------------------------------------------------------------ */

function buildOpenTextEntries(value) {
  const grouped = {}
  const metadataKeys = new Set(['average', 'question'])

  Object.keys(value || {}).forEach((key) => {
    if (metadataKeys.has(key)) return

    const text = String(key || '').trim()
    if (text.length < 3) return
    // Ignora o rótulo da pergunta e outros metadados (valor não numérico).
    if (!Number.isFinite(Number(value[key]))) return

    const count = Number(value[key])
    const normalized = normalizeText(text)
    if (normalized === 'question') return

    if (grouped[normalized] == undefined) {
      grouped[normalized] = { text, count: 0 }
    }

    grouped[normalized].count += count > 0 ? count : 1
  })

  return Object.values(grouped)
}

/**
 * Classifica um comentário em duas dimensões independentes: a polaridade
 * (positiva, negativa, mista ou neutra) e a presença de sugestão. Separar as
 * duas evita que um elogio com pedido de melhoria seja contado como elogio puro
 * e desapareça da lista de sugestões.
 */
function classifyText(text) {
  const normalized = normalizeText(text)
  const hasPositive = matchesAnyTerm(normalized, POSITIVE_TERMS)
  const hasNegative = matchesAnyTerm(normalized, NEGATIVE_TERMS)
  const hasSuggestion = matchesAnyTerm(normalized, SUGGESTION_TERMS)

  let polarity = 'neutral'
  if (hasPositive && hasNegative) polarity = 'mixed'
  else if (hasPositive) polarity = 'positive'
  else if (hasNegative) polarity = 'negative'

  return { polarity, hasSuggestion }
}

function emptySentiment() {
  return {
    positive: 0,
    neutral: 0,
    negative: 0,
    mixed: 0,
    sugestion: 0,
    positiveCount: 0,
    neutralCount: 0,
    negativeCount: 0,
    mixedCount: 0,
    sugestionCount: 0,
    sentimentScore: 0,
    sentimentScoreLevel: { label: '--', color: '#6c757d' },
    impactScore: 0,
    impactScoreLevel: { label: '--', color: '#6c757d' },
    confidenceScore: 0,
    confidenceScoreLevel: { label: '--', color: '#6c757d' },
    priorityScore: 0,
    priorityScoreLevel: { label: '--', color: '#6c757d' },
    total: 0,
    uniqueTotal: 0,
    classifiedTexts: { positive: [], neutral: [], sugestion: [], negative: [] },
  }
}

function getSentimentScoreLevel(value) {
  if (value >= 80) return { label: 'Muito positivo', color: '#146c43' }
  if (value >= 60) return { label: 'Positivo', color: '#20824f' }
  if (value >= 40) return { label: 'Neutro', color: '#e67e22' }
  if (value >= 20) return { label: 'Negativo', color: '#d93051' }
  return { label: 'Crítico', color: '#8b1a2b' }
}

function getPriorityScoreLevel(value) {
  if (value >= 60) return { label: 'Crítica', color: '#8b1a2b' }
  if (value >= 40) return { label: 'Alta', color: '#d93051' }
  if (value >= 20) return { label: 'Média', color: '#e67e22' }
  return { label: 'Baixa', color: '#20824f' }
}

function getImpactScoreLevel(value) {
  if (value >= 80) return { label: 'Muito alto', color: '#146c43' }
  if (value >= 60) return { label: 'Alto', color: '#20824f' }
  if (value >= 40) return { label: 'Médio', color: '#e67e22' }
  if (value >= 20) return { label: 'Baixo', color: '#d93051' }
  return { label: 'Irrelevante/ruído', color: '#6c757d' }
}

function getConfidenceScoreLevel(value) {
  if (value >= 40) return { label: 'Muito alta', color: '#146c43' }
  if (value >= 25) return { label: 'Alta', color: '#20824f' }
  if (value >= 15) return { label: 'Média', color: '#e67e22' }
  if (value >= 8) return { label: 'Baixa', color: '#d93051' }
  return { label: 'Irrelevante/ruído', color: '#6c757d' }
}

/**
 * O índice de sentimento pondera apenas a polaridade: positivas valem 1,
 * neutras e mistas 0,5 e negativas 0. As sugestões formam uma dimensão
 * paralela e por isso não entram na conta — antes elas valiam zero, o que
 * derrubava o índice de meses em que os visitantes elogiavam e pediam
 * melhorias no mesmo comentário.
 */
function analyzeSentiment(entries, maxWeight, universeWeight) {
  const list = Array.isArray(entries) ? entries : []
  if (!list.length) return emptySentiment()

  const weights = { positive: 0, neutral: 0, negative: 0, mixed: 0 }
  const counts = { positive: 0, neutral: 0, negative: 0, mixed: 0 }
  const classifiedTexts = {
    positive: [],
    neutral: [],
    sugestion: [],
    negative: [],
  }
  let suggestionWeight = 0
  let suggestionCount = 0
  let total = 0

  list.forEach((entry) => {
    const weight = toWeight(entry.count)
    const { polarity, hasSuggestion } = classifyText(entry.text)

    weights[polarity] += weight
    counts[polarity] += 1
    total += weight

    if (hasSuggestion) {
      suggestionWeight += weight
      suggestionCount += 1
      classifiedTexts.sugestion.push(entry.text)
    }

    if (polarity === 'positive') {
      classifiedTexts.positive.push(entry.text)
    } else if (polarity === 'negative' || polarity === 'mixed') {
      classifiedTexts.negative.push(entry.text)
    } else if (!hasSuggestion) {
      classifiedTexts.neutral.push(entry.text)
    }
  })

  const score =
    ((weights.positive + 0.5 * (weights.neutral + weights.mixed)) / total) * 100

  const impactBase = Number(maxWeight) > 0 ? Number(maxWeight) : total
  const confidenceBase =
    Number(universeWeight) > 0 ? Number(universeWeight) : total
  const impactScore = Number(((total / impactBase) * 100).toFixed(2))
  const confidenceScore = Number(((total / confidenceBase) * 100).toFixed(2))
  const priorityScore = Number((impactScore * (1 - score / 100)).toFixed(2))
  const percent = (weight) => Number(((weight / total) * 100).toFixed(2))

  return {
    positive: percent(weights.positive),
    neutral: percent(weights.neutral),
    negative: percent(weights.negative + weights.mixed),
    mixed: percent(weights.mixed),
    sugestion: percent(suggestionWeight),
    positiveCount: counts.positive,
    neutralCount: counts.neutral,
    negativeCount: counts.negative + counts.mixed,
    mixedCount: counts.mixed,
    sugestionCount: suggestionCount,
    sentimentScore: Number(score.toFixed(2)),
    sentimentScoreLevel: getSentimentScoreLevel(score),
    impactScore,
    impactScoreLevel: getImpactScoreLevel(impactScore),
    confidenceScore,
    confidenceScoreLevel: getConfidenceScoreLevel(confidenceScore),
    priorityScore,
    priorityScoreLevel: getPriorityScoreLevel(priorityScore),
    total,
    uniqueTotal: list.length,
    classifiedTexts,
  }
}

function buildWordCloud(entries) {
  const freq = {}

  entries.forEach((entry) => {
    const weight = toWeight(entry.count)
    const words = normalizeText(entry.text)
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)

    words.forEach((word) => {
      if (word.length < 4) return
      if (isIgnoredWordCloudTerm(word)) return
      freq[word] = (freq[word] || 0) + weight
    })
  })

  return Object.keys(freq)
    .map((word) => ({ word, count: freq[word] }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)
}

function parseExtraTopics(extraTopics) {
  return String(extraTopics || '')
    .split(',')
    .map((topic) => topic.trim())
    .filter((topic) => topic.length > 1)
}

/**
 * Junta os temas padrão com os digitados pelo gestor. Cada termo informado
 * cria um tema novo na análise (com impacto, confiança e prioridade próprios),
 * sem substituir nem fundir com os temas padrão.
 */
function buildTopicDefinitions(extraTerms) {
  const topics = DEFAULT_TOPICS.map((topic) => ({ ...topic }))

  extraTerms.forEach((term) => {
    const normalized = normalizeText(term)
    if (!normalized) return

    topics.push({
      key: `extra-${normalized.replace(/\s+/g, '-')}`,
      label: term,
      // Frase completa: "poluição sonora" casa o trecho inteiro no comentário.
      terms: [normalized],
      custom: true,
    })
  })

  return topics
}

function extractTopics(entries, extraTerms) {
  const definitions = buildTopicDefinitions(extraTerms)
  const universeWeight = totalWeight(entries)

  const matched = definitions.map((definition) => {
    const topicEntries = entries.filter((entry) =>
      matchesAnyTerm(normalizeText(entry.text), definition.terms)
    )

    return {
      key: definition.key,
      word: definition.label,
      custom: !!definition.custom,
      entries: topicEntries,
      count: totalWeight(topicEntries),
    }
  })

  const maxWeight = matched.reduce(
    (max, topic) => Math.max(max, topic.count),
    0
  )

  return matched
    .filter((topic) => topic.count > 0 || topic.custom)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count
      if (a.custom !== b.custom) return a.custom ? 1 : -1
      return String(a.word).localeCompare(String(b.word), 'pt-BR')
    })
    .map((topic) => ({
      key: topic.key,
      word: topic.word,
      custom: topic.custom,
      count: topic.count,
      texts: topic.entries.map((entry) => entry.text),
      ...analyzeSentiment(topic.entries, maxWeight || 1, universeWeight || 1),
    }))
}

function processSuggestions(entries, extraTopics) {
  if (!entries.length) {
    return { sentiment: emptySentiment(), wordCloud: [], mainTopics: [] }
  }

  return {
    sentiment: analyzeSentiment(entries),
    wordCloud: buildWordCloud(entries),
    mainTopics: extractTopics(entries, parseExtraTopics(extraTopics)),
  }
}

/* ------------------------------------------------------------------ *
 * Números-chave
 * ------------------------------------------------------------------ */

function pickNumber(value, fallback) {
  const parsed = Number(value)
  if (Number.isFinite(parsed) && parsed >= 0) {
    return { value: parsed, provisional: false }
  }

  return { value: fallback, provisional: true }
}

function ratio(part, whole) {
  if (!whole) return null
  return Number(((part / whole) * 100).toFixed(2))
}

function buildKeyNumbers(data, answered) {
  const visitors = pickNumber(
    data.total_visitors,
    COVERAGE_FALLBACK.total_visitors
  )
  const opened = pickNumber(
    data.total_responses_opened,
    COVERAGE_FALLBACK.total_responses_opened
  )
  const refunded = pickNumber(
    data.total_refunded,
    COVERAGE_FALLBACK.total_refunded
  )

  return {
    visitors,
    opened,
    refunded,
    answered: { value: answered, provisional: false },
    openRate: ratio(opened.value, visitors.value),
    answerRate: ratio(answered, opened.value),
    coverage: ratio(answered, visitors.value),
  }
}

function buildNegativeTail(questions) {
  const totals = questions.reduce(
    (acc, question) => {
      acc.negative += question.negative
      acc.total += question.total
      return acc
    },
    { negative: 0, total: 0 }
  )

  const worst = questions
    .slice()
    .sort((a, b) => b.negativeShare - a.negativeShare)
    .slice(0, 3)

  return {
    negative: totals.negative,
    total: totals.total,
    share: totals.total
      ? Number(((totals.negative / totals.total) * 100).toFixed(2))
      : 0,
    worst,
  }
}

/* ------------------------------------------------------------------ *
 * Relatório
 * ------------------------------------------------------------------ */

export async function fetchNPSReport(payload) {
  const response = await nodeService.getNPSData(payload)
  const data = (response && response.data) || {}
  const rawQuestions = data.data || {}
  const slugTitles = buildSlugTitles()

  const questions = []
  const responsesCount = {}
  let openTextEntries = []
  let openTextQuestion = null

  Object.keys(rawQuestions).forEach((rawSlug) => {
    let slug = resolveSurveySlug(rawSlug, slugTitles)

    if (slugTitles[slug] == undefined) {
      slugTitles[slug] = {
        title: humanizeSlugTitle(slug),
        session: '',
        group: slug,
        order: 900,
        scale: 0,
        type: 'radio',
        active: true,
      }
    }

    const meta = slugTitles[slug]
    if (meta.active === false) return

    const value = rawQuestions[rawSlug]
    if (!value || typeof value !== 'object') return

    if (slug === OPEN_TEXT_SLUG || meta.type === 'textarea') {
      openTextEntries = buildOpenTextEntries(value)
      responsesCount[slug] = totalWeight(openTextEntries)
      openTextQuestion = {
        slug,
        title: meta.title,
        session: meta.session || '',
        total: responsesCount[slug],
        uniqueTotal: openTextEntries.length,
      }
      return
    }

    if (NON_CHART_TYPES.indexOf(meta.type) >= 0) return

    const question = buildRatingQuestion(slug, meta, value)
    if (!question) return

    responsesCount[slug] = question.total
    questions.push(question)
  })

  questions.sort((a, b) => a.order - b.order)

  const answered = Number(data.total_responses) || 0
  const official = questions.find((q) => q.slug === OFFICIAL_SCORE_SLUG) || null

  return {
    ...data,
    questions,
    responsesCount,
    official,
    keyNumbers: buildKeyNumbers(data, answered),
    negativeTail: buildNegativeTail(questions),
    openTextQuestion,
    processedSuggestions: processSuggestions(
      openTextEntries,
      payload.extraTopics || ''
    ),
  }
}

export default { fetchNPSReport }
